'use client';

import { useLocale, useTranslations } from 'next-intl';
import { Card, CardHeader, CardTitle, CardContent, Link } from '@/components/ui';
import { LocalDateTime } from '@/components/ui/LocalDateTime';
import { formatDate } from '@/lib/utils';
import { resolveParticipantLabel } from '@/app/[locale]/event/[eventId]/libs/participantLabel';
import type { ScoredMatch } from '@/models/scoredMatchesModel';

interface ScoredByMeSectionProps {
  matches: ScoredMatch[];
}

/**
 * Matches the viewer scored but did not play in (parity with the app's Record tab).
 * They have no rating row for the viewer, so there are no rating columns. Renders
 * nothing when empty.
 */
export function ScoredByMeSection({ matches }: ScoredByMeSectionProps) {
  const t = useTranslations('Matches');
  const locale = useLocale();
  if (matches.length === 0) return null;
  const isJapanese = locale === 'ja-JP';

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('scoredByMe')}</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <ul className="divide-border divide-y">
          {matches.map((m) => {
            const score =
              m.player1Games != null && m.player2Games != null
                ? `${m.player1Games}–${m.player2Games}`
                : null;
            return (
              <li key={m.id} className="flex items-center justify-between gap-3 px-4 py-3">
                <div className="min-w-0 space-y-0.5 text-sm">
                  <LocalDateTime
                    iso={m.dateISO}
                    locale={locale}
                    format={formatDate}
                    className="text-muted block text-xs"
                  />
                  <div className="truncate font-medium">
                    <span>{resolveParticipantLabel({ player: m.player1, isJapanese })}</span>
                    {' · '}
                    <span>{resolveParticipantLabel({ player: m.player2, isJapanese })}</span>
                  </div>
                  {m.eventName && <div className="text-muted truncate text-xs">{m.eventName}</div>}
                </div>
                <div className="flex shrink-0 items-center gap-3">
                  {score && <span className="text-sm font-semibold tabular-nums">{score}</span>}
                  <Link href={`/match/scorecard/${m.id}`} variant="filled" color="default" size="sm">
                    {t('viewScorecard')}
                  </Link>
                </div>
              </li>
            );
          })}
        </ul>
      </CardContent>
    </Card>
  );
}
